import type { ReactNode } from 'react'
import { Text } from '@mantine/core'
import type { JobDoGateway, PassoDoJob } from '@/api/whatsappGateways'
import {
  LinhaDoTempoDePassos, type EstadoDoPasso, type PassoDaLinha,
} from '@/components/common/LinhaDoTempoDePassos'
import { formatDuration } from '@/components/admin/tenants/jobs/stepMeta'
import { PASSOS, PASSO_ROLLBACK, ROTULO_KIND, partesDoErro, rotuloDoPasso } from './passosDoJob'

const ESTADO: Record<PassoDoJob['status'], EstadoDoPasso> = {
  success: 'concluido', running: 'em_curso', failed: 'falhou',
}
const TETO_MIN = 15  // provisioner.ESPERA_MAXIMA

function ordem(job: JobDoGateway): string[] {
  const base = [...PASSOS[job.kind]]
  // O rollback só existe quando o rollout do `update` falhou: entra depois da espera.
  if (job.passos?.some((p) => p.passo === PASSO_ROLLBACK)) {
    base.splice(base.indexOf('aguardando_gateway') + 1, 0, PASSO_ROLLBACK)
  }
  // `infra` (o worker não montou os clientes AWS) não é passo da sequência, mas o
  // `falhar_passos` grava a linha dele: sem isto a falha sumiria da tela, e o botão junto.
  const fora = (job.passos ?? []).map((p) => p.passo).filter((p) => !base.includes(p))
  return [...fora, ...base]
}

function Erro({ codigo }: { codigo: string | null }) {
  const p = partesDoErro(codigo)
  if (!p) return <Text size="sm">Falhou sem código gravado</Text>
  return (
    <Text size="sm">
      {p.frase ?? 'Código de erro'}{' '}
      <Text span ff="monospace" size="xs" c="dimmed">({p.codigo})</Text>
    </Text>
  )
}

function haQuantosMin(iniciado: string, agora: number): string {
  const min = Math.max(0, Math.floor((agora - new Date(iniciado).getTime()) / 60_000))
  return `há ${min} min de ${TETO_MIN}`
}

/**
 * Linha do tempo de um job do gateway (spec 2026-10-15 §4.3): o último, no detalhe, ou
 * qualquer um, na aba "Operações" — lá a linha da lista já diz a operação e `titulo` vem
 * `null`. Devolve `null` para job sem passos (anterior à MIGRATION_393): quem chama
 * mostra a linha única de antes.
 */
export function LinhaDoTempoDoJob({ job, acaoNoPassoFalho, agora = Date.now(), titulo }: {
  job: JobDoGateway
  acaoNoPassoFalho?: ReactNode
  agora?: number
  /** Omitido: "Última operação: <kind>". `null`: sem título. */
  titulo?: string | null
}) {
  if (!job.passos || job.passos.length === 0) return null
  const porPasso = new Map(job.passos.map((p) => [p.passo, p]))
  const anteriores = new Map((job.passos_anteriores ?? []).map((p) => [p.passo, p]))

  const passos: PassoDaLinha[] = ordem(job).map((chave) => {
    const row = porPasso.get(chave)
    const doAnterior = !row ? anteriores.get(chave) : undefined
    const status: EstadoDoPasso = row ? ESTADO[row.status] : doAnterior ? 'concluido' : 'pendente'
    return {
      chave,
      rotulo: rotuloDoPasso(job.kind, chave),
      status,
      descricao: doAnterior ? 'Concluído no job anterior'
        : row?.status === 'running' ? haQuantosMin(row.iniciado_em, agora) : undefined,
      iniciado_em: (row ?? doAnterior)?.iniciado_em,
      concluido_em: (row ?? doAnterior)?.concluido_em,
      tentativas: row?.tentativas,
      erro: row?.status === 'failed' ? <Erro codigo={row.ultimo_erro} /> : undefined,
      testId: `passo-${chave}`,
    }
  })

  return (
    <>
      {titulo !== null && <Text size="sm" fw={600}>{titulo ?? `Última operação: ${ROTULO_KIND[job.kind]}`}</Text>}
      <LinhaDoTempoDePassos
        grupos={[{ chave: job.kind, passos }]}
        formatarDuracao={formatDuration}
        testId="linha-do-tempo-do-job"
        acao={(p) => (p.status === 'falhou' ? acaoNoPassoFalho ?? null : null)}
      />
    </>
  )
}
